import React from 'react';
import { Props } from '.';
import Icon from '../../../../components/icon/Icon';
import { calculateGoals, countExclusions, countMissedShots } from '../../../../helpers/EventHelper';
import {
    EventTypeEnum,
    EventVm,
    ExclusionTypeEnum,
    ShotTypeEnum,
    TurnoverTypeEnum,
} from '../../../../utils/api';

class MatchLineupRow extends React.PureComponent<Props> {
    constructor(props: Props) {
        super(props);
        this.state = {};
    }

    public render = (): React.ReactElement => {
        const {
            match,
            cap,
            capNumber,
            events,
            isHomeTeam,
            initEvent,
            lineupPlayer,
            isSelectedKeeper,
            selectPlayer,
            activeEvent,
        } = this.props;

        const scoredGoals = calculateGoals(events);
        const missedShots = countMissedShots(events);
        const commitedExclusions = countExclusions(events);

        const teamId = match ? (isHomeTeam ? match.homeTeamId : match.awayTeamId) : null;

        // Must branch on typeId: CreateEventCommand/UpdateEventCommand always carry all nested
        // request objects; using || across shot/exclusion/turnover picked wrong ids (e.g. 0) when editing.
        let activeMatchPlayerId: number | undefined;
        if (activeEvent) {
            const t = activeEvent.typeId as EventTypeEnum;
            if (t === EventTypeEnum.Shot) {
                activeMatchPlayerId = activeEvent.shot?.takenById ?? activeEvent.shot?.takenBy?.id;
            } else if (t === EventTypeEnum.Exclusion) {
                activeMatchPlayerId =
                    activeEvent.exclusion?.excludedPlayerId ??
                    activeEvent.exclusion?.excludedPlayer?.id;
            } else if (t === EventTypeEnum.Turnover) {
                activeMatchPlayerId =
                    activeEvent.turnover?.lostPossesionPlayerId ??
                    activeEvent.turnover?.lostPossesionPlayer?.id;
            } else if (t === EventTypeEnum.Goalkeeper_Change) {
                activeMatchPlayerId = activeEvent.goalkeeperChange?.newGoalkeeperMatchPlayerId;
            } else if (t === EventTypeEnum.Card) {
                activeMatchPlayerId =
                    activeEvent.card?.cardedPlayerId ?? activeEvent.card?.cardedPlayer?.id;
            }
        }
        const isActivePlayer =
            !!lineupPlayer &&
            activeMatchPlayerId !== undefined &&
            Number(activeMatchPlayerId) === Number(lineupPlayer.id);
        const activeType = isActivePlayer ? activeEvent?.typeId : undefined;
        const editGoal = activeType === EventTypeEnum.Shot && !!activeEvent?.shot?.isGoal;
        const editNoGoal = activeType === EventTypeEnum.Shot && !activeEvent?.shot?.isGoal;
        const editExclusion = activeType === EventTypeEnum.Exclusion;
        const editTurnover = activeType === EventTypeEnum.Turnover;
        const editGk = activeType === EventTypeEnum.Goalkeeper_Change;
        const editCard = activeType === EventTypeEnum.Card;

        let isExcluded = false;
        const playerExclusions = events.filter(
            (x: EventVm): boolean => x.typeId === EventTypeEnum.Exclusion
        );
        if (playerExclusions.length > 2) {
            isExcluded = true;
        }
        if (
            playerExclusions.filter(
                (x: EventVm): boolean =>
                    x.exclusion?.typeId === ExclusionTypeEnum.Suspension ||
                    x.exclusion?.typeId === ExclusionTypeEnum.Brutality
            ).length > 0
        ) {
            isExcluded = true;
        }
        const className = `c-team__btn ${isSelectedKeeper ? 'is-keeper active' : ''} ${
            isExcluded ? 'disabled-button' : ''
        } ${editGk || editCard ? 'c-team__btn--editing' : ''}`;

        const playerCap = (
            <div key={1} className={className}>
                <div
                    role="button"
                    className="c-team__btn-inner"
                    onDoubleClick={
                        teamId && lineupPlayer && !isExcluded
                            ? (): void => selectPlayer(cap, teamId)
                            : undefined
                    }
                >
                    <span className="fake-svg">{capNumber}</span>
                </div>
            </div>
        );
        const noGoalButton = (
            <div
                key={2}
                onClick={
                    lineupPlayer && !isExcluded
                        ? (): void =>
                              initEvent(EventTypeEnum.Shot, {
                                  teamId: teamId,
                                  takenById: lineupPlayer.id,
                                  typeId: ShotTypeEnum.Regular_Attack,
                                  isGoal: false,
                              })
                        : undefined
                }
                className={`c-team__btn c-team__btn--no-goal ${
                    isExcluded ? 'disabled-button' : ''
                } ${editNoGoal ? 'c-team__btn--editing' : ''}`}
                role="button"
            >
                <div className="c-team__btn-inner">
                    <Icon name="icon-volleyball-ball" />
                    <span>
                        &nbsp;
                        {missedShots > 0 ? missedShots : ''}
                    </span>
                </div>
            </div>
        );
        const goalButton = (
            <div
                key={5}
                onClick={
                    lineupPlayer && !isExcluded
                        ? (): void =>
                              initEvent(EventTypeEnum.Shot, {
                                  teamId: teamId,
                                  takenById: lineupPlayer.id,
                                  typeId: ShotTypeEnum.Regular_Attack,
                                  isGoal: true,
                              })
                        : undefined
                }
                className={`c-team__btn c-team__btn--goal ${isExcluded ? 'disabled-button' : ''} ${
                    editGoal ? 'c-team__btn--editing' : ''
                }`}
                role="button"
            >
                <div className="c-team__btn-inner">
                    <Icon name="icon-volleyball-ball" />
                    <span>
                        &nbsp;
                        {scoredGoals > 0 ? scoredGoals : ''}
                    </span>
                </div>
            </div>
        );
        const exclusions = (
            <div
                key={3}
                onClick={
                    lineupPlayer
                        ? (): void =>
                              initEvent(EventTypeEnum.Exclusion, {
                                  teamId: teamId,
                                  excludedPlayerId: lineupPlayer.id,
                                  typeId: ExclusionTypeEnum.Exclusion,
                              })
                        : undefined
                }
                className={`c-team__btn ${editExclusion ? 'c-team__btn--editing' : ''}`}
                role="button"
            >
                <div className="c-team__btn-inner">
                    <Icon name="icon-times" />
                    <span>
                        &nbsp;
                        {commitedExclusions > 0 ? commitedExclusions : ''}
                    </span>
                </div>
            </div>
        );
        const turnovers = (
            <div
                key={4}
                onClick={
                    lineupPlayer && !isExcluded
                        ? (): void =>
                              initEvent(EventTypeEnum.Turnover, {
                                  teamId: teamId,
                                  lostPossesionPlayerId: lineupPlayer.id,
                                  typeId: TurnoverTypeEnum.Lost_Ball,
                              })
                        : undefined
                }
                className={`c-team__btn ${isExcluded ? 'disabled-button' : ''} ${
                    editTurnover ? 'c-team__btn--editing' : ''
                }`}
                role="button"
            >
                <div className="c-team__btn-inner">
                    <Icon name="icon-share" />
                </div>
            </div>
        );

        const render = [turnovers, exclusions, noGoalButton, goalButton, playerCap];

        return (
            <div
                className={`c-team__row c-team__row--${
                    isHomeTeam ? 'white' : 'blue'
                }-team c-team__row--single c-team__row-- ${lineupPlayer ? '' : 'disabled'}${
                    isActivePlayer ? ' c-team__row--active-event' : ''
                }`}
            >
                {render}
            </div>
        );
    };
}

export default MatchLineupRow;
